/**
 * Features Components
 *
 * Feature section components with mdxui-compliant interfaces.
 */

import { Features as FeaturesBase } from './features'
import type {
  FeaturesGridProps,
  FeaturesRightProps,
  FeaturesLeftProps,
  FeaturesShowcaseProps,
  FeaturesStickyProps,
  FeaturesGalleryProps,
  FeaturesCodeCardsProps,
  FeaturesSplitCodeProps,
  FeaturesStatsProps,
  FeaturesCodeTabsProps,
  FeaturesCodeCardSingleProps,
} from './types'

// Main Features component + discriminated union
export { Features } from './features'
export type { FeaturesProps } from './features'

// All types from types.ts
export type {
  // Shared sub-types
  FeatureItem,
  CodeWindowConfig,
  ButtonConfig,
  LearnMoreConfig,
  ContentBlock,
  CodeCard,
  CodeTabItem,
  // Base + Mixins
  FeaturesBaseProps,
  FeaturesArrayProps,
  FeaturesMediaProps,
  FeaturesButtonProps,
  // Variant interfaces
  FeaturesGridProps,
  FeaturesRightProps,
  FeaturesLeftProps,
  FeaturesShowcaseProps,
  FeaturesStickyProps,
  FeaturesGalleryProps,
  FeaturesCodeCardsProps,
  FeaturesSplitCodeProps,
  FeaturesStatsProps,
  FeaturesCodeCardSingleProps,
  FeaturesCodeTabsProps,
  // Union
  FeaturesVariant,
} from './types'

// Raw components
// (Dispatcher variant implementations — FeaturesGrid, FeaturesShowcase,
// FeaturesSticky, FeaturesGallery, FeaturesCodeCards, FeaturesCodeCardSingle,
// FeaturesSplitCode, FeaturesCodeTabs — are intentionally NOT re-exported here:
// those public names are the adapter wrappers defined below. The raw
// implementations live in their own files for direct, internal imports.)
export { FeaturesStickyHighlights, type FeaturesStickyHighlightsProps, type FeaturesStickyHighlightsHighlight, type FeaturesStickyHighlightsFeature } from './features-sticky-highlights'
export { FeaturesAgentCards, type FeaturesAgentCardsProps, type FeaturesAgentCardsAgent, type FeaturesAgentCardsHighlight } from './features-agent-cards'
export { FeaturesAgentFlow, type FeaturesAgentFlowProps } from './agent-flow'
export { FeaturesAgentTeam, type FeaturesAgentTeamProps, type FeaturesAgentTeamMember } from './agent-team'
export { FeaturesSplit, type FeaturesSplitProps } from './features-split'

// Showcase components (re-exported from showcase folder)
export { FeaturesShowcaseIcons, type FeaturesShowcaseIconsProps } from './showcase'
export { FeaturesCarousel } from './features-carousel'

// Stats
export { Stats, type StatsProps } from './stats'
export { Performance, type PerformanceProps, type PerformanceStat } from './stats'

// Grid components (re-exported from grid folder)
export {
  FeaturesRow,
  type FeaturesRowProps,
  FeaturesDividers,
  type FeaturesDividersProps,
  type FeaturesDividersFeature,
  FeaturesTimeline,
  type FeaturesTimelineProps,
  type FeaturesTimelineFeature,
} from './grid'

// =============================================================================
// Semantic Block Variants
// =============================================================================

/**
 * FeaturesGrid - 3-column grid layout with icons
 * Maps to FeaturesGrid component
 */
export function FeaturesGrid(props: Omit<FeaturesGridProps, 'variant'>) {
  return <FeaturesBase {...props} variant="grid" />
}

/**
 * FeaturesRight - Split layout with content left, image/code right
 * (FeaturesSplit position="right")
 */
export function FeaturesRight(props: Omit<FeaturesRightProps, 'variant'>) {
  return <FeaturesBase {...props} variant="right" />
}

/**
 * FeaturesLeft - Split layout with image/code left, content right
 * (FeaturesSplit position="left")
 */
export function FeaturesLeft(props: Omit<FeaturesLeftProps, 'variant'>) {
  return <FeaturesBase {...props} variant="left" />
}

/**
 * FeaturesShowcase - Numbered showcase cards with images and bullet points
 * Maps to FeaturesShowcase from shadcnblocks
 */
export function FeaturesShowcase(props: Omit<FeaturesShowcaseProps, 'variant'>) {
  return <FeaturesBase {...props} variant="showcase" />
}

/**
 * FeaturesSticky - Sticky header with scrolling feature cards
 * Maps to FeaturesSticky from shadcnblocks
 */
export function FeaturesSticky(props: Omit<FeaturesStickyProps, 'variant'>) {
  return <FeaturesBase {...props} variant="sticky" />
}

/**
 * FeaturesGallery - 2x2 grid with stacked images and text content blocks
 * Maps to FeaturesGallery from shadcnblocks
 */
export function FeaturesGallery(props: Omit<FeaturesGalleryProps, 'variant'>) {
  return <FeaturesBase {...props} variant="gallery" />
}

/**
 * FeaturesCodeCards - 2-column grid with borderless code windows and text content
 * Maps to FeaturesCodeCards from shadcnblocks
 */
export function FeaturesCodeCards(props: Omit<FeaturesCodeCardsProps, 'variant'>) {
  return <FeaturesBase {...props} variant="code-cards" />
}

/**
 * FeaturesSplitCode - Split layout with content left, code window right with gradient overlay
 * Maps to FeaturesSplitCode from shadcnblocks
 */
export function FeaturesSplitCode(props: Omit<FeaturesSplitCodeProps, 'variant'>) {
  return <FeaturesBase {...props} variant="split-code" />
}

/**
 * FeaturesStats - Stats row with 3-4 metric items
 * Maps to Stats component
 */
export function FeaturesStats(props: Omit<FeaturesStatsProps, 'variant'>) {
  return <FeaturesBase {...props} variant="stats" />
}

/**
 * FeaturesCodeTabs - Tab-based feature switcher with code windows per tab
 * Maps to FeaturesCodeTabs component
 */
export function FeaturesCodeTabs(props: Omit<FeaturesCodeTabsProps, 'variant'>) {
  return <FeaturesBase {...props} variant="code-tabs" />
}

/**
 * FeaturesCodeCardSingle - Single code card with muted background
 * Maps to FeaturesCodeCardSingle component
 */
export function FeaturesCodeCardSingle(props: Omit<FeaturesCodeCardSingleProps, 'variant'>) {
  return <FeaturesBase {...props} variant="code-card-single" />
}
